import {
  type DragCancelEvent,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import type { LeadBoardItem } from '@/core/components/leads/leads-board-item';
import { scrollDroppedLeadCardIntoView } from '@/core/components/leads/leads-board-scroll';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';

type BoardColumn = { id: string; name: string };

type UseLeadsBoardDndOptions = {
  columns: BoardColumn[];
  boardDataRef: MutableRefObject<LeadBoardItem[]>;
  dragStartSnapshotRef: MutableRefObject<LeadBoardItem[] | null>;
  boardScrollRef: RefObject<HTMLDivElement | null>;
  setBoardData: Dispatch<SetStateAction<LeadBoardItem[]>>;
  setIsDragging: Dispatch<SetStateAction<boolean>>;
  setLockedBoardColumnHeight: Dispatch<SetStateAction<number | null>>;
  setActiveCardId: Dispatch<SetStateAction<string | null>>;
  persistBoard: () => Promise<void>;
};

export function useLeadsBoardDnd({
  columns,
  boardDataRef,
  dragStartSnapshotRef,
  boardScrollRef,
  setBoardData,
  setIsDragging,
  setLockedBoardColumnHeight,
  setActiveCardId,
  persistBoard,
}: UseLeadsBoardDndOptions) {
  const sensors = useDndProfileSensors();
  const lastOverIdRef = useRef<string | number | null>(null);
  /** Track the last column the dragged card was over so haptic only fires on column change. */
  const lastOverColumnRef = useRef<string | null>(null);
  /**
   * Projected index of the dragged card within its column.
   * Recomputed on every pointer move from live DOM rects so it reflects
   * CSS-transformed positions (i.e. other cards that have shifted to make room).
   * A change here == a real position swap → haptic fires.
   */
  const lastProjectedIndexRef = useRef<number | null>(null);
  /**
   * Tracks whether actual drag movement happened during the current drag
   * session. Used to gate the drop haptic so a pure activation+release
   * (no movement) doesn't double-buzz.
   */
  const dragMovedRef = useRef(false);

  /**
   * Element map built once at drag-start: id → DOM element for every
   * [data-lead-id] card. Used by handleDragMove to avoid a querySelector
   * per sibling per pointer event (O(1) lookup instead of O(n) DOM scan).
   */
  const elemMapRef = useRef<Map<string, HTMLElement>>(new Map());
  /**
   * rAF handle for the throttled drag-move handler. A value !== null means
   * a frame is already scheduled; new events update the pending center-Y
   * instead of scheduling another frame.
   */
  const dragMoveRafRef = useRef<number | null>(null);
  /**
   * Most-recent activeCenter-Y value captured in handleDragMove. Consumed
   * inside the scheduled rAF callback so we always process the latest position.
   */
  const pendingDragMoveCenterYRef = useRef<{
    activeId: string;
    activeCenterY: number;
  } | null>(null);

  /**
   * Native capture-phase listener added while a drag is active.
   * Fires before dnd-kit's window/document bubble-phase pointermove listeners,
   * silencing secondary-pointer (second finger) move events so dnd-kit never
   * sees them and the drag stays anchored to the first finger.
   * We deliberately do NOT call preventDefault() so the browser can still
   * use the second touch for horizontal board scrolling.
   */
  const blockSecondaryPointerRef = useRef<((e: PointerEvent) => void) | null>(
    null,
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      lastOverIdRef.current = null;
      lastOverColumnRef.current = null;
      lastProjectedIndexRef.current = null;
      dragMovedRef.current = false;

      // Install secondary-pointer blocker before dnd-kit's own listeners so it
      // runs in the capture phase (highest priority).
      const blocker = (e: PointerEvent) => {
        if (!e.isPrimary) e.stopPropagation();
      };
      blockSecondaryPointerRef.current = blocker;
      // Block all secondary-pointer events that could confuse dnd-kit:
      // - pointermove: dnd-kit would update drag position from the wrong finger
      // - pointerup / pointercancel: dnd-kit would drop the card prematurely
      // Horizontal board scroll for the second finger is handled manually via
      // TouchEvents so we no longer need native pointer-based scroll here.
      document.addEventListener('pointermove', blocker, { capture: true });
      document.addEventListener('pointerup', blocker, { capture: true });
      document.addEventListener('pointercancel', blocker, { capture: true });

      // Lock body + ion-content so the page doesn't scroll under the finger
      // while dragging. The board container stays scrollable so dnd-kit's
      // built-in autoScroll can drive it toward the edges automatically.
      lockBodyScrollDuringDrag();
      if (boardScrollRef.current) {
        boardScrollRef.current.style.scrollSnapType = 'none';
      }
      // Haptic at activation = "card is now draggable" (iOS long-press pattern).
      // Quick taps never reach this handler because the PointerSensor's delay
      // (300ms on mobile) suppresses activation. The global button-haptic is
      // opted out on the card via `data-no-haptic="true"` so taps stay silent.
      void triggerDragStartHaptic();
      setActiveCardId(event.active.id as string);
      const card = boardDataRef.current.find(
        (item) => item.id === event.active.id,
      );
      if (card) {
        const container = boardScrollRef.current;
        if (container) {
          let maxColumnHeight = 0;
          for (const columnEl of container.querySelectorAll<HTMLElement>(
            '[data-column-id]',
          )) {
            maxColumnHeight = Math.max(maxColumnHeight, columnEl.offsetHeight);
          }
          setLockedBoardColumnHeight(
            maxColumnHeight > 0 ? maxColumnHeight : null,
          );
        } else {
          setLockedBoardColumnHeight(null);
        }
        setIsDragging(true);
        dragStartSnapshotRef.current = [...boardDataRef.current];

        // Build the id→element map once per drag session for fast O(1) lookup
        // in handleDragMove, avoiding a querySelector per sibling per frame.
        const map = new Map<string, HTMLElement>();
        document
          .querySelectorAll<HTMLElement>('[data-lead-id]')
          .forEach((el) => {
            const id = el.dataset.leadId;
            if (id) map.set(id, el);
          });
        elemMapRef.current = map;
      }
    },
    [
      boardDataRef,
      boardScrollRef,
      dragStartSnapshotRef,
      setActiveCardId,
      setIsDragging,
      setLockedBoardColumnHeight,
    ],
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      dragMovedRef.current = true;

      const { active } = event;
      const activeRect = active.rect.current.translated;
      if (!activeRect) return;

      // Capture position immediately (rect is a live mutable ref that will be
      // updated on the next pointer event, so we read it now).
      const activeId = String(active.id);
      const activeCenterY = activeRect.top + activeRect.height / 2;

      // Store the latest values so the rAF callback always works with the most
      // recent position even if several pointer events arrive before a frame.
      pendingDragMoveCenterYRef.current = { activeId, activeCenterY };

      // If a frame is already scheduled, just update the pending data and return.
      if (dragMoveRafRef.current != null) return;

      // Schedule the DOM-reading work for the next animation frame so at most
      // one layout measurement happens per rendered frame (rAF throttle).
      dragMoveRafRef.current = requestAnimationFrame(() => {
        dragMoveRafRef.current = null;
        const pending = pendingDragMoveCenterYRef.current;
        if (!pending) return;

        const { activeId: id, activeCenterY: centerY } = pending;
        const activeItem = boardDataRef.current.find((i) => i.id === id);
        if (!activeItem) return;

        // Compute the projected index of the dragged card within its column by
        // counting how many sibling cards are visually above it right now.
        // getBoundingClientRect() accounts for CSS transforms that dnd-kit applies
        // as cards shift to make room — so this works even when over.id never
        // changes (e.g. dragging back to the original position).
        // elemMapRef provides O(1) element lookup without a querySelector per sibling.
        const columnSiblings = boardDataRef.current.filter(
          (i) => i.column === activeItem.column && i.id !== id,
        );

        const projectedIndex = columnSiblings.filter((sibling) => {
          const el = elemMapRef.current.get(sibling.id);
          if (!el) return false;
          const rect = el.getBoundingClientRect();
          return rect.top + rect.height / 2 < centerY;
        }).length;

        if (
          lastProjectedIndexRef.current !== null &&
          projectedIndex !== lastProjectedIndexRef.current
        ) {
          void triggerDragOverHaptic();
        }
        lastProjectedIndexRef.current = projectedIndex;
      });
    },
    [boardDataRef],
  );

  const handleBoardDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (over == null) {
        lastOverIdRef.current = null;
        return;
      }
      lastOverIdRef.current = over.id;

      // Multi-column board: when hovering a different status, we must optimistically move the active
      // item into that column during drag so that Sortable can compute indices and show reordering
      // transforms immediately (same UX as the single-list modules).
      const activeId = String(active.id);
      const overIdStr = String(over.id);
      const activeItem = boardDataRef.current.find(
        (item) => item.id === activeId,
      );
      if (!activeItem) return;

      const overItem = boardDataRef.current.find(
        (item) => item.id === overIdStr,
      );
      const overColumn: string =
        overItem?.column ??
        columns.find((col) => col.id === overIdStr)?.id ??
        activeItem.column;

      const isColumnChange = overColumn !== lastOverColumnRef.current;

      if (isColumnChange) {
        lastOverColumnRef.current = overColumn;
        lastProjectedIndexRef.current = null; // reset so intra-column index is recalculated fresh in new column
        // Skip haptic for the very first assignment (same column as drag origin).
        if (overColumn !== activeItem.column) {
          void triggerDragOverHaptic();
        }
      }

      if (overColumn === activeItem.column) {
        // Same-column haptic is handled continuously in handleDragMove so it
        // also fires when the direction reverses (onDragOver wouldn't re-fire).
        //
        // However, for cross-column drops we still need to keep the optimistic
        // position up-to-date as the card moves over different cards inside the
        // new column. Without this update the card lands at whatever position
        // was set when it first entered the column, not where it was released.
        //
        // We only re-insert when hovering directly over another card (not the
        // column container) to avoid thrashing boardData unnecessarily.
        if (overItem && overItem.column === overColumn) {
          const byColumn = new Map<string, LeadBoardItem[]>();
          for (const col of columns) {
            byColumn.set(col.id, []);
          }
          for (const item of boardDataRef.current) {
            if (!byColumn.has(item.column)) byColumn.set(item.column, []);
            byColumn.get(item.column)!.push(item);
          }

          const destItems = byColumn.get(overColumn) ?? [];
          const oldIdx = destItems.findIndex((i) => i.id === activeId);
          const newIdx = destItems.findIndex((i) => i.id === overIdStr);

          if (oldIdx >= 0 && newIdx >= 0 && oldIdx !== newIdx) {
            const reordered = arrayMove(destItems, oldIdx, newIdx);
            byColumn.set(overColumn, reordered);

            const flattened: LeadBoardItem[] = [];
            for (const col of columns) {
              flattened.push(...(byColumn.get(col.id) ?? []));
            }
            for (const [colId, items] of byColumn.entries()) {
              if (!columns.some((c) => c.id === colId))
                flattened.push(...items);
            }

            boardDataRef.current = flattened;
            setBoardData(flattened);
          }
        }
        return;
      }

      const byColumn = new Map<string, LeadBoardItem[]>();
      for (const col of columns) {
        byColumn.set(col.id, []);
      }
      for (const item of boardDataRef.current) {
        if (!byColumn.has(item.column)) byColumn.set(item.column, []);
        byColumn.get(item.column)!.push(item);
      }

      // Remove active from its current column list
      for (const [colId, items] of byColumn.entries()) {
        const idx = items.findIndex((i) => i.id === activeId);
        if (idx >= 0) {
          items.splice(idx, 1);
          byColumn.set(colId, items);
          break;
        }
      }

      const destItems = byColumn.get(overColumn) ?? [];
      const insertIndex =
        overItem && overItem.column === overColumn
          ? Math.max(
              0,
              destItems.findIndex((i) => i.id === overItem.id),
            )
          : destItems.length;

      const nextActive = { ...activeItem, column: overColumn };
      destItems.splice(insertIndex, 0, nextActive);
      byColumn.set(overColumn, destItems);

      const flattened: LeadBoardItem[] = [];
      for (const col of columns) {
        flattened.push(...(byColumn.get(col.id) ?? []));
      }
      for (const [colId, items] of byColumn.entries()) {
        if (!columns.some((c) => c.id === colId)) flattened.push(...items);
      }

      boardDataRef.current = flattened;
      setBoardData(flattened);
    },
    [boardDataRef, columns, setBoardData],
  );

  const cleanupActiveDragSession = useCallback(() => {
    const blocker = blockSecondaryPointerRef.current;
    if (blocker) {
      document.removeEventListener('pointermove', blocker, { capture: true });
      document.removeEventListener('pointerup', blocker, { capture: true });
      document.removeEventListener('pointercancel', blocker, { capture: true });
      blockSecondaryPointerRef.current = null;
    }
    unlockBodyScrollAfterDrag();
    // Cancel any pending drag-move rAF before restoring scroll state, so no
    // frame callback can fire after the drag session has formally ended.
    if (dragMoveRafRef.current != null) {
      cancelAnimationFrame(dragMoveRafRef.current);
      dragMoveRafRef.current = null;
    }
    pendingDragMoveCenterYRef.current = null;
    elemMapRef.current.clear();
    if (boardScrollRef.current) {
      boardScrollRef.current.style.scrollSnapType = '';
    }
    dragMovedRef.current = false;
    setActiveCardId(null);
    lastOverIdRef.current = null;
    lastOverColumnRef.current = null;
    lastProjectedIndexRef.current = null;
    setIsDragging(false);
    setLockedBoardColumnHeight(null);
  }, [
    boardScrollRef,
    setActiveCardId,
    setIsDragging,
    setLockedBoardColumnHeight,
  ]);

  useEffect(() => {
    const scrollRef = boardScrollRef;
    return () => {
      const blocker = blockSecondaryPointerRef.current;
      if (blocker) {
        document.removeEventListener('pointermove', blocker, { capture: true });
        document.removeEventListener('pointerup', blocker, { capture: true });
        document.removeEventListener('pointercancel', blocker, {
          capture: true,
        });
        blockSecondaryPointerRef.current = null;
      }
      unlockBodyScrollAfterDrag();
      const container = scrollRef.current;
      if (container?.style.scrollSnapType === 'none') {
        container.style.scrollSnapType = '';
      }
    };
  }, [boardScrollRef]);

  const handleBoardDragCancel = useCallback(
    (_event: DragCancelEvent) => {
      const snapshot = dragStartSnapshotRef.current;
      if (snapshot) {
        boardDataRef.current = snapshot;
        setBoardData(snapshot);
      }
      dragStartSnapshotRef.current = null;
      cleanupActiveDragSession();
    },
    [
      boardDataRef,
      cleanupActiveDragSession,
      dragStartSnapshotRef,
      setBoardData,
    ],
  );

  const handleBoardDragEnd = useCallback(
    (event: DragEndEvent) => {
      const hadMovement = dragMovedRef.current;
      cleanupActiveDragSession();
      if (hadMovement) {
        void triggerDragEndHaptic();
      }

      const droppedActiveId = String(event.active.id);

      const { active, over } = event;

      if (over && active.id !== over.id) {
        const activeId = String(active.id);
        const overIdStr = String(over.id);
        const activeItem = boardDataRef.current.find(
          (item) => item.id === activeId,
        );

        if (activeItem) {
          const overItem = boardDataRef.current.find(
            (item) => item.id === overIdStr,
          );

          // Apply the final sort order via arrayMove for both same-column
          // reorders AND cross-column drops. For cross-column drags the
          // optimistic update in handleBoardDragOver already assigned the card
          // to the target column, so activeItem.column is the destination here.
          // Without this, cross-column cards always land at the bottom because
          // only the first handleBoardDragOver entry position is persisted.
          if (overItem && activeItem.column === overItem.column) {
            const colId = activeItem.column;
            const colItems = boardDataRef.current.filter(
              (i) => i.column === colId,
            );
            const oldIdx = colItems.findIndex((i) => i.id === activeId);
            const newIdx = colItems.findIndex((i) => i.id === overIdStr);
            if (oldIdx >= 0 && newIdx >= 0 && oldIdx !== newIdx) {
              const reordered = arrayMove(colItems, oldIdx, newIdx);
              const flattened: LeadBoardItem[] = [];
              for (const col of columns) {
                if (col.id === colId) {
                  flattened.push(...reordered);
                } else {
                  flattened.push(
                    ...boardDataRef.current.filter((i) => i.column === col.id),
                  );
                }
              }
              boardDataRef.current = flattened;
              setBoardData(flattened);
            }
          }
        }
      }

      if (hadMovement && boardScrollRef.current) {
        const scrollContainer = boardScrollRef.current;
        const finalData = boardDataRef.current;
        const droppedItem = finalData.find((i) => i.id === droppedActiveId);
        const isDesktop =
          typeof window !== 'undefined' &&
          window.matchMedia('(min-width: 768px)').matches;

        if (droppedItem) {
          // Wait for scroll-snap restore + React re-render before measuring DOM.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const cardEl = scrollContainer.querySelector<HTMLElement>(
                `[data-lead-id="${droppedActiveId}"]`,
              );
              if (!cardEl) return;
              scrollDroppedLeadCardIntoView(cardEl, scrollContainer, {
                isDesktop,
              });
            });
          });
        }
      }

      void persistBoard();
      dragStartSnapshotRef.current = null;
    },
    [
      boardDataRef,
      boardScrollRef,
      cleanupActiveDragSession,
      columns,
      dragStartSnapshotRef,
      persistBoard,
      setBoardData,
    ],
  );

  return {
    sensors,
    handleDragStart,
    handleDragMove,
    handleBoardDragOver,
    handleBoardDragEnd,
    handleBoardDragCancel,
  };
}
